import { Editor, Range, Point, Element, Transforms } from 'slate';

const LIST_TYPES = ['orderedList', 'unorderedList'];

/**
 * Plugin for list handling: Enter creates new list item, Backspace at start merges/unwraps
 * @param {Editor} editor - Slate editor instance
 * @returns {Editor} Extended editor with list handling
 */
const withLists = (editor) => {
    const { insertBreak, deleteBackward } = editor;

    editor.insertBreak = () => {
        const [listItemEntry] = Editor.nodes(editor, {
            match: (n) => !Editor.isEditor(n) && Element.isElement(n) && n.type === 'list-item',
        });

        if (listItemEntry) {
            // Standard behavior: always split to create new list item (with number/bullet)
            Transforms.splitNodes(editor, {
                match: (n) => Element.isElement(n) && n.type === 'list-item',
                always: true,
            });
            return;
        }

        insertBreak();
    };

    editor.deleteBackward = (unit) => {
        const { selection } = editor;

        if (selection && Range.isCollapsed(selection)) {
            const [listItemEntry] = Editor.nodes(editor, {
                match: (n) => !Editor.isEditor(n) && Element.isElement(n) && n.type === 'list-item',
            });

            if (listItemEntry) {
                const [listItem, listItemPath] = listItemEntry;
                const start = Editor.start(editor, listItemPath);

                if (Point.equals(selection.anchor, start)) {
                    const [listEntry] = Editor.nodes(editor, {
                        match: (n) => Element.isElement(n) && LIST_TYPES.includes(n.type),
                        mode: 'lowest',
                    });

                    if (listEntry) {
                        const [list, listPath] = listEntry;
                        const isOnlyItem = list.children.length === 1;

                        Editor.withoutNormalizing(editor, () => {
                            if (isOnlyItem) {
                                // Only item: convert to paragraph and unwrap list
                                Transforms.setNodes(editor, { type: 'paragraph' }, { at: listItemPath });
                                Transforms.unwrapNodes(editor, {
                                    at: listPath,
                                    match: (n) => Element.isElement(n) && LIST_TYPES.includes(n.type),
                                });
                                // Cursor stays at start (default behavior)
                            } else {
                                // First, middle, or last item: lift out and convert to paragraph
                                // This keeps cursor at start of the converted line instead of merging upward
                                const listItemRef = Editor.pathRef(editor, listItemPath);
                                Transforms.liftNodes(editor, { at: listItemPath });
                                const liftedPath = listItemRef.current;
                                listItemRef.unref();
                                if (liftedPath) {
                                    Transforms.setNodes(editor, { type: 'paragraph' }, { at: liftedPath });
                                }
                                // Cursor automatically stays at start of converted paragraph
                            }
                        });
                        return;
                    }
                }
            }
        }

        deleteBackward(unit);
    };

    return editor;
};

export default withLists;
